import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Navigation } from '../Navigation';
import { CustomCursor } from '../CustomCursor';
import { ScrollProgress } from '../ui/ScrollProgress';

interface BestofLayoutProps {
  isLoaded: boolean;
}

export function BestofLayout({ isLoaded }: BestofLayoutProps) {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Clean up ScrollTrigger on route change
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      // Kill all ScrollTriggers when route changes
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      // Scroll to top
      window.scrollTo(0, 0);
      // Refresh after new page renders
      const timer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);

      prevPathRef.current = location.pathname;
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  return (
    <>
      {/* Scroll progress indicator */}
      <ScrollProgress />

      {/* Custom cursor */}
      <CustomCursor />

      {/* Navigation */}
      <Navigation isLoaded={isLoaded} />

      {/* Main content with page transitions */}
      <main>
        <AnimatePresence mode="wait">
          <Outlet key={location.pathname} />
        </AnimatePresence>
      </main>

      {/* Noise overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-[50] opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
    </>
  );
}
